// src/components/SeasonManualRating.js
import React from "react";

/**
 * Manual re-rating step used during End Of Season flow.
 * - Loads current season players (passed in as props)
 * - Shows current price (read-only)
 * - Allows admin to re-rate using same logic as CreateLeague
 * - Computes proposed new season starting price
 *
 * IMPORTANT:
 * - This component does NOT write to Firestore
 * - It only mutates local state passed from EndOfSeasonReview
 */

const POSITIONS = ["DEF", "MID", "FWD"];
const RATINGS = [1, 2, 3, 4, 5];

// Same pricing logic as CreateLeague
const DEFAULT_PRICES = {
  DEF: 5.0,
  MID: 5.5,
  FWD: 6.0,
};

const RATING_ADJ = {
  1: -1.0,
  2: -0.5,
  3: 0.0,
  4: 0.5,
  5: 1.0,
};

const computePrice = (position, rating) => {
  if (!position || !rating) return null;
  return Number(
    ((DEFAULT_PRICES[position] ?? 0) + (RATING_ADJ[rating] ?? 0)).toFixed(1)
  );
};

export default function SeasonManualRating({ players = [], setPlayers }) {
  React.useEffect(() => {
    if (!Array.isArray(players) || typeof setPlayers !== "function") return;

    setPlayers((prev) => {
      if (!Array.isArray(prev)) return prev;

      let didChange = false;

      const next = prev.map((p) => {
        if (p?.newPrice != null) return p;

        const initialPrice =
          computePrice(p.position, p.rating) ??
          p.originalPrice ??
          p.currentPrice ??
          null;

        didChange = true;
        return { ...p, newPrice: initialPrice };
      });

      return didChange ? next : prev;
    });
  }, [players?.length]);

  if (!Array.isArray(players)) {
    return <div>Loading players…</div>;
  }

  const updatePlayer = (id, updates) => {
    setPlayers((prev) =>
      prev.map((p) => {
        if (p.id !== id) return p;

        const updated = { ...p, ...updates };

        // Recompute proposed new price when position or rating changes
        updated.newPrice = computePrice(updated.position, updated.rating);

        return updated;
      })
    );
  };

  return (
    <div style={{ display: "grid", gap: 12, maxWidth: 720 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "2fr 90px 80px 110px 110px",
          fontWeight: 700,
          gap: 12,
        }}
      >
        <div>Player</div>
        <div>Position</div>
        <div>Rating</div>
        <div style={{ textAlign: "center" }}>Current $</div>
        <div style={{ textAlign: "center" }}>New $</div>
      </div>

      {players.map((player) => (
        <div
          key={player.id}
          style={{
            display: "grid",
            gridTemplateColumns: "2fr 90px 80px 110px 110px",
            gap: 12,
            alignItems: "center",
          }}
        >
          <div style={{ fontWeight: 600 }}>{player.name}</div>

          {/* Position */}
          <select
            value={player.position}
            onChange={(e) =>
              updatePlayer(player.id, { position: e.target.value })
            }
          >
            {POSITIONS.map((pos) => (
              <option key={pos} value={pos}>
                {pos}
              </option>
            ))}
          </select>

          {/* Rating */}
          <select
            value={player.rating}
            onChange={(e) =>
              updatePlayer(player.id, { rating: Number(e.target.value) })
            }
          >
            {RATINGS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>

          {/* Current season ending price */}
          <div style={{ textAlign: "center" }}>${Number(player.currentPrice).toFixed(1)}</div>

          {/* Proposed new season starting price */}
          <div style={{ fontWeight: 600, textAlign: "center" }}>
            ${player.newPrice?.toFixed(1)}
          </div>
        </div>
      ))}
    </div>
  );
}